// @ts-check
import React, { useState } from 'react';
import IconPlus from '../../utils/icon/icon-plus.svg';
import IconMinus from '../../utils/icon/icon-minus.svg';

/**
 * @typedef {Object} FacetValue
 * @property {string} label - The label of the facet value.
 * @property {string} value - The value of the facet value.
 * @property {boolean} active - Whether the facet value is active.
 * @property {number} count - The count of the facet value.
 * @property {string} paramName - The parameter name of the facet value.
 * @property {string} urlToRemove - The URL to remove the facet value.
 * @property {string} urlToAdd - The URL to add the facet value.
 */

/**
 * @typedef {Object} Facet
 * @property {string} label - The label of the facet.
 * @property {string} type - The type of the facet.
 * @property {string} operator - The operator of the facet.
 * @property {string} paramName - The parameter name of the facet.
 * @property {string} urlToRemove - The URL to remove the facet.
 * @property {FacetValue[]} values - The values of the facet.
 * @property {FacetValue[]} activeValues - The active values of the facet.
 * @property {FacetValue[]} inactiveValues - The inactive values of the facet.
 */

/**
 * List Facet component for filter
 * @component
 * @param {Object} props - The facet to render.
 * @param {Facet} props.facet - The label of the facet.
 * @param {(event: Event) => void} props.onChange - The function to call when the sort option changes.
 * @param {boolean} props.isLoading - Whether the filters are loading.
 */

const ListFacet = ({ facet, onChange, isLoading }) => {
  const { label, values, activeValues } = facet;
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => setIsOpen(!isOpen);

  return (
    <details
      className="cursor-pointer rounded-lg bg-brand-light-beige [&[open]>summary]:pb-5"
      open={isOpen}
      onToggle={handleToggle}
    >
      <summary
        role="button"
        className="body-1-bold flex items-center justify-between px-4 py-6"
      >
        <div className="flex h-[22px] w-full max-w-[calc(100%-theme(spacing.3))] shrink-0 grow-0 basis-[calc(100%-theme(spacing.3))] flex-col justify-center">
          <span>{label}</span>
          {!isOpen && activeValues.length > 0 && (
            <div>
              <div className="body-3 truncate pr-2">{activeValues.map((value) => value.label).join(', ')}</div>
            </div>
          )}
        </div>
        <span className="size-3 [&>svg]:size-3">{isOpen ? <IconMinus /> : <IconPlus />}</span>
      </summary>
      <div className="px-4 pb-5">
        {values.map((value) => (
          <FacetInput
            key={value.value}
            facetValue={value}
            onChange={onChange}
            isLoading={isLoading}
          />
        ))}
      </div>
    </details>
  );
};

export default ListFacet;

/**
 * List Facet component for filter
 * @component
 * @param {Object} props - The facet to render.
 * @param {FacetValue} props.facetValue - The label of the facet.
 * @param {(e: Event, args: { value: string, paramName: string, urlToAdd: string, urlToRemove: string, count: number, active: boolean }) => void} props.onChange - The function to call when the sort option changes.
 * @param {boolean} props.isLoading - Whether the filters are loading.
 */

const FacetInput = ({ facetValue, onChange, isLoading }) => {
  const { value, label, active, paramName, urlToAdd, urlToRemove, count } = facetValue;
  return (
    <div key={value}>
      <label className="t flex cursor-pointer items-center gap-2 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50">
        <input
          type="checkbox"
          className="size-3 appearance-none rounded bg-transparent text-default focus:ring-default disabled:cursor-not-allowed disabled:opacity-50"
          checked={active}
          value={value}
          onChange={(e) => onChange(e, { value, paramName, urlToAdd, urlToRemove, count, active })}
          data-param-name={paramName}
          data-url-to-add={urlToAdd}
          data-url-to-remove={urlToRemove}
          data-count={count}
          disabled={count === 0 || isLoading}
        />
        <span className="heading-5 select-none">{label}</span>
      </label>
    </div>
  );
};
